import RemoveGhostIcon from "@shared/svg/remove-ghost.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import { Counter, InputBox, InputLabel, Input } from "@shared/ui/Inputs";
import NotFoundCard from "@shared/ui/NotFound";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import { SingleSelectDropdown } from "@shared/ui/Dropdowns";
import { PartListItem } from "@shared/ui/ListItemCards";
import {
  ExternalIcon,
  EXTERNAL_ICON_SIZES,
  EXTERNAL_ICON_THUMB_BADGE_CLASS,
} from "@shared/ui/Indicators";
import _ from "lodash";
import {
  MinusCircle,
  Trash,
  DotsThree,
  PencilSimple,
  ArrowsClockwise,
  WarningCircle,
  MagnifyingGlass,
} from "phosphor-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import ReactTooltip from "react-tooltip";
import { Waypoint } from "react-waypoint";

import {
  getMappedCustomFields,
  getPartThumbnail,
  getSortedCustomFields,
} from "#/src/utils";
import PAID_FEATURES from "$/settings/paid-features.json";
import { NotFoundComponent } from "~/components/_notFoundMessage";
import { ActionPopup } from "~/components/_popupMenu";
import CustomFieldsAccordion from "~/components/customFields/_customFieldsAccordion";
import { useAuth } from "~/components/general";
import PartPreview from "~/components/inventory/PartPreview";
import {
  InfoIcon,
  NoUserAssignedIcon,
  PendingAllocationWarningIcon,
} from "~/icons/icons";
import { getEnums } from "~/utils";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

const paidFeatures = getEnums(PAID_FEATURES, "reference");

const ALLOCATION_STATUS = Object.freeze({
  ALLOCATED: "ALLOCATED",
  PENDING: "PENDING",
});

// Skeleton placeholder that mirrors the PartListItem card layout
// (thumbnail + two text lines on the left, counter block on the right).
const PartListSkeleton = ({ rows = 5 }) => (
  <section aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div
        key={index}
        className="flex items-center justify-between p-md mb-lg border border-solid border-primary bg-primary rounded-lg animate-pulse"
      >
        <div className="flex items-center gap-lg w-1/2">
          <div className="h-4xl w-5xl shrink-0 rounded-lg bg-accent" />
          <div className="flex flex-col gap-xs w-full overflow-hidden">
            <div className="h-md w-1/3 rounded bg-accent" />
            <div className="h-lg w-2/3 rounded bg-accent" />
          </div>
        </div>
        <div className="h-3xl w-24 shrink-0 rounded-lg bg-accent" />
      </div>
    ))}
  </section>
);

export const EditPartQuantityComp = ({
  part,
  currentQuantity,
  originalQuantity,
  onQuantityChange,
  inventoryCustomAdditionalFields = [],
  oem = null,
  onStockWarningsChange = (hasWarnings) => {},
  onClickDetails = null,
}) => {
  const { messages } = useIntl();
  const { user } = useAuth();
  const appConfig = useGetAppConfigValue();
  const [quantity, setQuantity] = useState(currentQuantity);
  const [partForModalPreview, setPartForModalPreview] = useState(null);
  const [partForModalPreviewCustomFields, setPartForModalPreviewCustomFields] =
    useState([]);
  const componentIsMountedRef = useRef(true);

  // Use originalQuantity if provided, otherwise fallback to currentQuantity
  const baseQuantity =
    originalQuantity !== undefined ? originalQuantity : currentQuantity;

  useEffect(() => {
    componentIsMountedRef.current = true;
    return () => {
      componentIsMountedRef.current = false;
    };
  }, []);

  const isStockFeatureEnabled = isPaidFeatureAvailable(
    paidFeatures.inventoryStockManagement,
    oem ? { oem } : user,
    appConfig,
    oem == null,
  );

  useEffect(() => {
    setQuantity(currentQuantity);
  }, [currentQuantity]);

  // Handle modal preview custom fields
  useEffect(() => {
    if (partForModalPreview && inventoryCustomAdditionalFields?.length > 0) {
      const { customFieldArr } = getMappedCustomFields(
        partForModalPreview?.customFields ?? [],
        inventoryCustomAdditionalFields ?? [],
      );

      const arrDiff = _.differenceBy(
        inventoryCustomAdditionalFields,
        customFieldArr,
        "label",
      );

      if (!componentIsMountedRef.current) return;

      setPartForModalPreviewCustomFields(
        getSortedCustomFields([...customFieldArr, ...arrDiff]),
      );
    } else if (partForModalPreview?.customFields?.length > 0) {
      const customFields = partForModalPreview.customFields;

      if (!componentIsMountedRef.current) return;

      setPartForModalPreviewCustomFields(
        getSortedCustomFields(
          customFields
            .filter(
              (field) =>
                field.value?.toString()?.length > 0 || field.values?.length > 0,
            )
            .map((field) => ({
              ...field.fieldId,
              value: field.value,
              values: field.values,
            })),
        ),
      );
    }
  }, [partForModalPreview, inventoryCustomAdditionalFields]);

  const getAvailableStock = useCallback(() => {
    const rawValue = part?.availableStock;
    if (rawValue === null || rawValue === undefined) {
      return 0;
    }
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.max(0, numericValue);
  }, [part]);

  const formatStockLimitMessage = useCallback(
    (count) => {
      const normalizedCount = Number.isFinite(Number(count))
        ? Number(count)
        : 0;
      const template = messages?.inventory?.stock?.errors?.insufficientStock;
      const replacedTemplate = template?.replace(
        /{{count}}|{count}/g,
        normalizedCount.toString(),
      );

      if (
        replacedTemplate === template &&
        !template?.includes("{{count}}") &&
        !template?.includes("{count}")
      ) {
        return `Insufficient Stock: Please be aware, there are only ${normalizedCount} parts available in inventory.`;
      }

      return replacedTemplate;
    },
    [messages],
  );

  // Validate stock for edit mode
  // Net change = new quantity - ORIGINAL assigned quantity
  // Check if net change exceeds available stock
  const getStockValidation = useCallback(() => {
    if (!isStockFeatureEnabled) {
      return { message: "", isBlocking: false, variant: null };
    }

    const availableStock = getAvailableStock();
    const alreadyAssignedStock = baseQuantity;
    const netChange = quantity - baseQuantity;
    const totalAvailableStock = availableStock + alreadyAssignedStock;
    const outOfStockMessage = messages?.inventory?.stock?.errors?.outOfStock;

    // If reducing quantity or no change, no validation needed
    if (netChange <= 0) {
      return { message: "", isBlocking: false, variant: null };
    }

    // Check if new quantity exceeds total available stock
    if (quantity > totalAvailableStock) {
      if (totalAvailableStock === 0) {
        return {
          message: outOfStockMessage,
          isBlocking: true,
          variant: "danger",
        };
      }

      return {
        message: formatStockLimitMessage(totalAvailableStock),
        isBlocking: true,
        variant: "warning",
      };
    }

    return { message: "", isBlocking: false, variant: null };
  }, [
    isStockFeatureEnabled,
    getAvailableStock,
    quantity,
    baseQuantity,
    messages,
    formatStockLimitMessage,
  ]);

  const stockValidation = getStockValidation();

  useEffect(() => {
    onStockWarningsChange(stockValidation.isBlocking);
  }, [stockValidation.isBlocking, onStockWarningsChange]);

  const handleDec = () => {
    const updatedQty = Math.max(quantity - 1, 0);
    setQuantity(updatedQty);
    onQuantityChange(updatedQty);
  };

  const handleInc = () => {
    const updatedQty = quantity + 1;
    setQuantity(updatedQty);
    onQuantityChange(updatedQty);
  };

  const handleInput = (value) => {
    let numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      numericValue = 0;
    }

    const sanitizedValue = Math.floor(numericValue);
    setQuantity(sanitizedValue);
    onQuantityChange(sanitizedValue);
  };

  const handleRemovePartQuantity = () => {
    setQuantity(0);
    onQuantityChange(0);
  };

  const hasQuantity = quantity > 0;
  const previewCurrentQuantity = quantity;
  const hasPartForPreviewQuantity = hasQuantity;
  const previewWarningMessage = stockValidation.message;
  const previewWarningVariant = stockValidation.variant;

  return partForModalPreview ? (
    <>
      <div className="u-width-100 u-padding-x-5">
        <img
          className="part-modal-preview-image"
          src={getPartThumbnail(partForModalPreview, true)}
          alt={partForModalPreview?.name}
        />
        <div className="u-width-100 u-flex u-margin-b-4">
          <div className="u-width-6 u-margin-r-1 u-overflow-hidden">
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
              {messages?.inventory.partName + ":"}
            </BodyText>
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
              {partForModalPreview.name}
            </BodyText>
          </div>
          <div className="u-width-6 u-overflow-hidden">
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
              {messages?.inventory.labels.articleNumber + ":"}
            </BodyText>
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
              {partForModalPreview.articleNumber}
            </BodyText>
          </div>
        </div>
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
          {messages?.common.description + ":"}
        </BodyText>
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="mb-md">
          {partForModalPreview?.description || "-"}
        </BodyText>
        {partForModalPreviewCustomFields.length > 0 && (
          <div className="u-margin-b-6">
            <div className="divider divider--gray u-margin-b-2" />
            <CustomFieldsAccordion
              hasAccordion={false}
              isEditable={false}
              customFieldList={partForModalPreviewCustomFields}
              selectFieldValueClassname="part-modal-preview-select-custom-field-value"
              selectFieldLabelClassname="part-modal-preview-select-custom-field-label"
            />
          </div>
        )}
      </div>
      <div className=" u-width-100 u-flex u-justify-center part-modal-preview-footer-wrapper">
        {hasPartForPreviewQuantity && (
          <button
            className="btn-v2 ghost-btn-v2 parts-inc-dec-btn u-margin-r-2"
            onClick={handleRemovePartQuantity}
          >
            <Trash size={16} />
          </button>
        )}
        <Counter
          count={previewCurrentQuantity}
          setCount={handleInput}
          incrementCount={handleInc}
          decrementCount={handleDec}
          isReadOnlyInput={false}
        />
        {previewWarningMessage ? (
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color={
              previewWarningVariant === "danger"
                ? "text-danger"
                : "text-warning"
            }
            className="mt-sm text-center"
          >
            {previewWarningMessage}
          </BodyText>
        ) : null}
      </div>
    </>
  ) : (
    <>
      {isStockFeatureEnabled && (
        <section>
          <div
            className={`w-full flex items-center p-md mb-4 rounded-lg bg-brand-lightest`}
          >
            <InfoIcon
              width={16}
              height={16}
              className="stroke-brand"
              strokeColor="stroke-brand"
            />
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="ml-sm">
              {messages?.inventory?.stock?.editQuantityInfoMessage ||
                "Updating the part quantity here will adjust the available stock for this part accordingly."}
            </BodyText>
          </div>
        </section>
      )}

      <section>
        <div>
          <PartListItem
            articleNumber={part?.articleNumber}
            name={part?.name}
            thumbnail={getPartThumbnail(part)}
            visibilityScope={part?.visibilityScope}
            onButtonClick={
              onClickDetails ? () => setPartForModalPreview(part) : undefined
            }
            showDeleteButton={hasQuantity}
            onDeleteButtonClick={handleRemovePartQuantity}
            hideDetailsButton={!onClickDetails}
            counterProps={{
              count: quantity,
              setCount: handleInput,
              incrementCount: handleInc,
              decrementCount: handleDec,
              isReadOnlyInput: false,
            }}
            errorMessage={stockValidation.message}
            errorVariant={stockValidation.variant}
          />
        </div>
      </section>
    </>
  );
};

export const AddPartComp = ({
  addedParts,
  handleAddPart,
  allParts = [],
  onClickDetails,
  partForModalPreview,
  inventoryCustomAdditionalFields = [],
  syncStates = false,
  oem = null,
  onStockWarningsChange = (hasWarnings) => {},
  hideSearch = false,
  validateStock = true,
  // When false, stock warnings still render on each row but never block the
  // parent's submit (e.g. Support Tickets, where parts don't touch inventory).
  blockOnStockWarning = true,
  bannerMessage = null,
  showBanner = null,
  selectedAsset = null,
  assetOptions = [],
  onAssetChange = null,
  isLoadingParts = false,
  emptyClassName = "",
  keyByAsset = false,
  // Opt-in server-driven list (paginated catalog). When `onSearch` is provided
  // the parent owns filtering (server-side), so local name/articleNumber
  // filtering is bypassed. When `onScrollToBottom` is provided a sentinel is
  // rendered at the list end to drive infinite scroll. Both default off, so
  // existing consumers (asset-scoped, fully-loaded lists) are unaffected.
  onScrollToBottom = null,
  onSearch = null,
}) => {
  const { messages } = useIntl();
  const { user } = useAuth();
  const appConfig = useGetAppConfigValue();
  const addedPartsIds = Object.keys(addedParts);
  const isServerSearch = typeof onSearch === "function";
  // Server-driven catalogs render the list inside a fixed-height scroll area
  // that carries its own scrollbar. The search field and each row need a
  // matching right gutter so their right edges line up with the scrollbar
  // instead of the rows tucking underneath it.
  const isScrollableList =
    isServerSearch || typeof onScrollToBottom === "function";

  // Server search is debounced, so the input value and the data the server has
  // returned drift apart between keystroke and response. `appliedSearch` tracks
  // the term the current `allParts` actually reflect; while it differs from the
  // input (or the query is loading) the list is "pending" and we show the
  // skeleton instead of an empty state — otherwise clearing the field would
  // briefly show "no parts" against the previous (now-stale) result set.
  const [appliedSearch, setAppliedSearch] = useState("");

  // Debounce server search so we don't refetch on every keystroke. The input
  // stays controlled by local `searchValue` (immediate); the network call and
  // `appliedSearch` update together when the debounce fires.
  const debouncedSearch = useMemo(
    () =>
      isServerSearch
        ? _.debounce((value) => {
            onSearch(value);
            setAppliedSearch(value);
          }, 350)
        : null,
    [isServerSearch, onSearch],
  );
  useEffect(() => () => debouncedSearch?.cancel(), [debouncedSearch]);

  // When keyByAsset is enabled, quantities are tracked per asset+part so the
  // same part can be added independently from different assets.
  const partKey = useCallback(
    (partId) =>
      keyByAsset && selectedAsset ? `${selectedAsset}::${partId}` : partId,
    [keyByAsset, selectedAsset],
  );

  const isStockFeatureEnabled =
    validateStock &&
    isPaidFeatureAvailable(
      paidFeatures.inventoryStockManagement,
      oem ? { oem } : user,
      appConfig,
      oem == null,
    );

  const shouldShowBanner =
    showBanner !== null ? showBanner : isStockFeatureEnabled;

  const [selectedParts, setSelectedParts] = useState(() => {
    const obj = {};
    allParts.map(({ part }) => {
      let qty = 0;
      if (syncStates && addedParts) {
        qty = addedParts?.[partKey(part._id)] || 0;
      }
      obj[partKey(part._id)] = qty;
      return part;
    });
    return obj;
  });

  // Update selectedParts when addedParts changes (for edit mode)
  useEffect(() => {
    if (syncStates && addedParts) {
      setSelectedParts((prev) => {
        const updated = { ...prev };
        Object.keys(addedParts).forEach((id) => {
          updated[id] = addedParts[id];
        });
        return updated;
      });
    }
  }, [addedParts, syncStates]);
  const [searchValue, setSearchValue] = useState("");
  const [partForModalPreviewCustomFields, setPartForModalPreviewCustomFields] =
    useState([]);
  const componentIsMountedRef = useRef(true);

  useEffect(() => {
    componentIsMountedRef.current = true;
    return () => {
      componentIsMountedRef.current = false;
    };
  }, []);

  const getAvailableLimit = useCallback((part) => {
    const rawValue = part?.availableStock;
    if (rawValue === null || rawValue === undefined) {
      return null;
    }

    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) return null;
    return Math.max(0, numericValue);
  }, []);

  const formatStockLimitMessage = useCallback(
    (count) => {
      const normalizedCount = Number.isFinite(Number(count))
        ? Number(count)
        : 0;
      const errors = messages?.inventory?.stock?.errors ?? {};
      // When the warning never blocks (Support Tickets), use the informational
      // wording that reassures the user their inventory stock is untouched.
      const template = blockOnStockWarning
        ? errors.insufficientStock
        : errors.insufficientStockTicket ?? errors.insufficientStock;
      const replacedTemplate = (template ?? "").replace(
        /{{count}}|{count}/g,
        normalizedCount.toString(),
      );

      if (
        replacedTemplate === template &&
        !template.includes("{{count}}") &&
        !template.includes("{count}")
      ) {
        return `Insufficient Stock: Please be aware, there are only ${normalizedCount} parts available in inventory.`;
      }

      return replacedTemplate;
    },
    [messages, blockOnStockWarning],
  );

  const getStockValidation = useCallback(
    (part, quantity) => {
      const errors = messages?.inventory?.stock?.errors ?? {};
      // Informational (non-blocking) wording for Support Tickets, WO wording
      // otherwise — mirrors formatStockLimitMessage's insufficient-stock case.
      const outOfStockMessage = blockOnStockWarning
        ? errors.outOfStock
        : errors.outOfStockTicket ?? errors.outOfStock;

      // Handle edit mode: check if the net change exceeds available stock
      if (part._editMode) {
        const originalStock = part._originalAvailableStock || 0;
        const currentAssigned = part._currentAssignedQuantity || 0;
        const netChange = quantity - currentAssigned;

        // If reducing quantity or no change, no validation needed
        if (netChange <= 0) {
          return { message: "", isBlocking: false, variant: null };
        }

        // Check if we have enough stock for the increase
        if (originalStock === 0 && netChange > 0) {
          return {
            message: outOfStockMessage,
            isBlocking: true,
            variant: "danger",
          };
        }

        if (netChange > originalStock) {
          return {
            message: formatStockLimitMessage(originalStock),
            isBlocking: true,
            variant: "warning",
          };
        }

        return { message: "", isBlocking: false, variant: null };
      }

      // Normal mode (adding parts)
      const limit = getAvailableLimit(part) || 0;

      if (limit === 0) {
        const isBlocking = quantity > 0;
        return { message: outOfStockMessage, isBlocking, variant: "danger" };
      }

      if (quantity > limit) {
        return {
          message: formatStockLimitMessage(limit),
          isBlocking: true,
          variant: "warning",
        };
      }

      return { message: "", isBlocking: false, variant: null };
    },
    [formatStockLimitMessage, getAvailableLimit, messages, blockOnStockWarning],
  );

  useEffect(() => {
    if (partForModalPreview && inventoryCustomAdditionalFields?.length > 0) {
      const { customFieldArr } = getMappedCustomFields(
        partForModalPreview?.customFields ?? [],
        inventoryCustomAdditionalFields ?? [],
      );

      const arrDiff = _.differenceBy(
        inventoryCustomAdditionalFields,
        customFieldArr,
        "label",
      );

      setPartForModalPreviewCustomFields(
        getSortedCustomFields([...customFieldArr, ...arrDiff]),
      );
    } else if (partForModalPreview?.customFields?.length > 0) {
      const customFields = partForModalPreview.customFields;
      setPartForModalPreviewCustomFields(
        getSortedCustomFields(
          customFields
            .filter(
              (field) =>
                field.value?.toString()?.length > 0 || field.values?.length > 0,
            )
            .map((field) => ({
              ...field.fieldId,
              value: field.value,
              values: field.values,
            })),
        ),
      );
    }
  }, [partForModalPreview]);

  const stockValidation = useMemo(() => {
    if (!isStockFeatureEnabled) {
      return { messages: {}, blockingIds: new Set() };
    }

    const messagesMap = {};
    const blockingIds = new Set();

    allParts.forEach(({ part }) => {
      const partId = part?._id;
      if (!partId) return;

      const quantity = selectedParts?.[partKey(partId)] || 0;
      const { message, isBlocking, variant } = getStockValidation(
        part,
        quantity,
      );

      if (message) {
        messagesMap[partKey(partId)] = {
          message,
          variant: variant || "warning",
        };
      }

      if (isBlocking && blockOnStockWarning) {
        blockingIds.add(partKey(partId));
      }
    });

    return { messages: messagesMap, blockingIds };
  }, [
    allParts,
    selectedParts,
    isStockFeatureEnabled,
    getStockValidation,
    partKey,
    blockOnStockWarning,
  ]);

  const stockWarnings = stockValidation.messages;
  const hasBlockingWarnings = stockValidation.blockingIds.size > 0;

  useEffect(() => {
    if (!componentIsMountedRef.current) return;
    if (!isStockFeatureEnabled) {
      onStockWarningsChange(false);
      return;
    }
    onStockWarningsChange(hasBlockingWarnings);
  }, [hasBlockingWarnings, isStockFeatureEnabled, onStockWarningsChange]);

  const availableParts = useMemo(() => {
    if (!addedPartsIds.length || syncStates) return allParts;

    return allParts.filter(
      ({ part }) => !addedPartsIds.includes(partKey(part?._id)),
    );
  }, [allParts, addedPartsIds, syncStates, partKey]);

  // The list is "pending" while a debounced search is in flight (input differs
  // from the applied term) or the query is loading. Show the skeleton then —
  // never an empty state derived from a half-updated search.
  const searchPending = isServerSearch && searchValue !== appliedSearch;
  const showSkeleton = isLoadingParts || searchPending;

  const handleDec = (part) => {
    if (!part?._id) return;
    const partId = part._id;
    const currentQty = selectedParts?.[partKey(partId)] || 0;
    const updatedQty = Math.max(currentQty - 1, 0);

    setSelectedParts((prev) => ({ ...prev, [partKey(partId)]: updatedQty }));

    handleAddPart(partId, updatedQty, part, selectedAsset);
  };

  const handleRemovePartQuantity = (part) => {
    if (!part?._id) return;
    const partId = part._id;

    setSelectedParts((prev) => ({ ...prev, [partKey(partId)]: 0 }));
    handleAddPart(partId, 0, part, selectedAsset);
  };

  const handleInc = (part) => {
    if (!part?._id) return;
    const partId = part._id;
    const currentQty = selectedParts?.[partKey(partId)] || 0;
    const updatedQty = currentQty + 1;

    setSelectedParts((prev) => ({ ...prev, [partKey(partId)]: updatedQty }));
    handleAddPart(partId, updatedQty, part, selectedAsset);
  };

  const handleInput = (value, part) => {
    if (!part?._id) return;
    const partId = part._id;

    let numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      numericValue = 0;
    }

    const sanitizedValue = Math.floor(numericValue);

    setSelectedParts((prev) => ({
      ...prev,
      [partKey(partId)]: sanitizedValue,
    }));
    handleAddPart(partId, sanitizedValue, part, selectedAsset);
  };

  const previewCurrentQuantity = partForModalPreview?._id
    ? selectedParts?.[partKey(partForModalPreview._id)] || 0
    : 0;
  const previewWarningData = partForModalPreview?._id
    ? stockWarnings[partKey(partForModalPreview._id)]
    : null;
  const previewWarningMessage = previewWarningData?.message;
  const previewWarningVariant = previewWarningData?.variant;
  const hasPartForPreviewQuantity =
    Boolean(partForModalPreview?._id) && previewCurrentQuantity > 0;

  return partForModalPreview ? (
    <>
      <div className="u-width-100 u-padding-x-5">
        <img
          className="part-modal-preview-image"
          src={getPartThumbnail(partForModalPreview, true)}
        />
        <div className="u-width-100 u-flex u-margin-b-4">
          <div className="u-width-6 u-margin-r-1 u-overflow-hidden">
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
              {messages?.inventory.partName + ":"}
            </BodyText>
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
              {partForModalPreview.name}
            </BodyText>
          </div>
          <div className="u-width-6 u-overflow-hidden">
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
              {messages?.inventory.labels.articleNumber + ":"}
            </BodyText>
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
              {partForModalPreview.articleNumber}
            </BodyText>
          </div>
        </div>
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
          {messages?.common.description + ":"}
        </BodyText>
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="mb-md">
          {partForModalPreview?.description || "-"}
        </BodyText>
        {partForModalPreviewCustomFields.length > 0 && (
          <div className="u-margin-b-6">
            <div className="divider divider--gray u-margin-b-2" />
            <CustomFieldsAccordion
              hasAccordion={false}
              isEditable={false}
              customFieldList={partForModalPreviewCustomFields}
              selectFieldValueClassname="part-modal-preview-select-custom-field-value"
              selectFieldLabelClassname="part-modal-preview-select-custom-field-label"
            />
          </div>
        )}
      </div>
      <div className=" u-width-100 u-flex u-justify-center part-modal-preview-footer-wrapper">
        {hasPartForPreviewQuantity && (
          <button
            className="btn-v2 ghost-btn-v2 parts-inc-dec-btn u-margin-r-2"
            onClick={() => handleRemovePartQuantity(partForModalPreview)}
          >
            <Trash size={16} />
          </button>
        )}
        <Counter
          count={previewCurrentQuantity}
          setCount={(val) => handleInput(val, partForModalPreview)}
          incrementCount={() => handleInc(partForModalPreview)}
          decrementCount={() => handleDec(partForModalPreview)}
          isReadOnlyInput={false}
        />
        {previewWarningMessage ? (
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color={
              previewWarningVariant === "danger"
                ? "text-danger"
                : "text-warning"
            }
            className="mt-sm text-center"
          >
            {previewWarningMessage}
          </BodyText>
        ) : null}
      </div>
    </>
  ) : (
    <>
      {shouldShowBanner && (
        <section>
          <div
            className={`w-full flex items-center p-md mb-4 rounded-lg bg-brand-lightest`}
          >
            <InfoIcon
              width={16}
              height={16}
              className="stroke-brand"
              strokeColor="stroke-brand"
            />

            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="ml-sm">
              {bannerMessage ||
                messages["inventory"]["stock"]["addPartInfoMessage"]}
            </BodyText>
          </div>
        </section>
      )}

      {/* Search + asset switcher stay mounted while the list loads, so the
          field never disappears and the dialog height doesn't jump. */}
      {!hideSearch && (
        <section
          className={`flex items-center gap-md mb-md ${
            isScrollableList ? "pr-sm" : ""
          }`}
        >
          <Input
            keyId="parts-search"
            className="flex-1 pl-3xl"
            wrapperClasses="w-full w-4/6"
            placeholder={messages["hierarchy"]["assets"]["searchParts"]}
            leadingIconConfig={{
              icon: <MagnifyingGlass size={16} />,
              className: "top-3",
            }}
            onChange={(e) => {
              setSearchValue(e.target.value);
              // Server-driven catalog: debounced refetch in the parent.
              if (debouncedSearch) debouncedSearch(e.target.value);
            }}
            value={searchValue}
          />
          {assetOptions && assetOptions.length > 1 && onAssetChange && (
            <div className="flex-shrink-0 w-2/6">
              <SingleSelectDropdown
                keyId="asset-selector-parts"
                options={assetOptions}
                values={
                  selectedAsset
                    ? [
                        {
                          value: selectedAsset,
                          label: assetOptions.find(
                            (opt) => opt.value === selectedAsset,
                          )?.label,
                        },
                      ]
                    : []
                }
                onChange={(selected) => {
                  if (selected.length > 0) {
                    onAssetChange(selected[0].value);
                  }
                }}
                placeholder="Select Asset"
                searchable={false}
              />
            </div>
          )}
        </section>
      )}

      {/* Server-driven catalog: bound the list to a fixed-height scroll area so
          the dialog height stays constant regardless of item count (and so the
          infinite-scroll sentinel has a scroll container to trigger in). */}
      <div
        className={
          isScrollableList
            ? "flex-1 min-h-[360px] max-h-[360px] overflow-y-auto new-scrollbar pr-sm"
            : ""
        }
      >
        {showSkeleton ? (
          <PartListSkeleton />
        ) : !availableParts.length ? (
          isServerSearch || onScrollToBottom ? (
            <NotFoundCard
              icon={<NoUserAssignedIcon className="w-16 h-16" />}
              title={
                appliedSearch
                  ? messages?.common?.noSearchResultsTitle
                  : messages?.inventory?.noPartsFoundV2
              }
              description={
                appliedSearch
                  ? messages?.common?.noSearchResultsDescription
                  : messages?.inventory?.noPartsFoundDescription
              }
              showDescription
              margin=""
              style={{ minHeight: "360px" }}
            />
          ) : (
            <NotFoundComponent
              icon={<NoUserAssignedIcon />}
              className={`no-tickets-wrapper full-page ${emptyClassName}`}
              title={messages["inventory"]["noPartsForAsset"]}
            />
          )
        ) : (
          <section className="[&>div>div]:!mb-md [&>div:last-of-type>div]:!mb-0">
            {(isServerSearch
              ? availableParts
              : availableParts.filter(
                  ({ part }) =>
                    part?.name
                      ?.toLowerCase()
                      ?.includes(searchValue.toLowerCase()) ||
                    part?.articleNumber
                      ?.toLowerCase()
                      ?.includes(searchValue.toLowerCase()),
                )
            ).map(({ part }) => {
              const hasQuantity = selectedParts[partKey(part._id)] > 0;
              const warningData = stockWarnings[partKey(part._id)];

              return (
                <div key={part._id}>
                  <PartListItem
                    articleNumber={part?.articleNumber}
                    name={part?.name}
                    thumbnail={getPartThumbnail(part)}
                    visibilityScope={part?.visibilityScope}
                    onButtonClick={() => onClickDetails(part)}
                    showDeleteButton={hasQuantity}
                    onDeleteButtonClick={() => handleRemovePartQuantity(part)}
                    hideDetailsButton={!onClickDetails}
                    counterProps={{
                      count: selectedParts?.[partKey(part._id)] || 0,
                      setCount: (val) => handleInput(val, part),
                      incrementCount: () => handleInc(part),
                      decrementCount: () => handleDec(part),
                      isReadOnlyInput: false,
                    }}
                    errorMessage={warningData?.message}
                    errorVariant={warningData?.variant}
                  />
                </div>
              );
            })}
            {/* Infinite-scroll sentinel for server-driven catalogs. */}
            {onScrollToBottom && <Waypoint onEnter={onScrollToBottom} />}
          </section>
        )}
      </div>
    </>
  );
};

export const AddedPartsComp = ({
  addedParts = [],
  handlePartRemove = () => {},
  onEditPart = () => {},
  onAllocatePart = () => {},
  allowPreview = false,
  isClosedTicket = false,
  partsCustomAdditionalFields = [],
  isRequest = false,
  allowEdit = true,
  showAddedBy = true,
  isInventoryManagementEnabled = false,
  // z-index for the remove-confirmation alert. Override when rendered inside a
  // sheet/overlay above the default z-20.
  alertZIndexClass = "z-20",
}) => {
  const { messages } = useIntl();
  const [showPartPreview, setShowPartPreview] = useState(false);
  const { user } = useAuth();
  const [activeMenuPartId, setActiveMenuPartId] = useState(null);

  const [showPartRemoveConfirmation, setShowPartRemoveConfirmation] = useState({
    show: false,
    name: "",
  });
  const tempRemovePartId = useRef(null);
  const partToPreview = useRef(null);

  const handlePartRemoveClick = (part) => {
    setShowPartRemoveConfirmation({ show: true, name: part.name });
    tempRemovePartId.current = part.id;
    setActiveMenuPartId(null);
  };

  const handleEditPartClick = (part, quantity) => {
    onEditPart({ part, quantity });
    setActiveMenuPartId(null);
  };

  const openPreview = (part) => {
    if (!allowPreview) return;

    partToPreview.current = part;
    setShowPartPreview(true);
  };

  return (
    <>
      <div className="flex text-xs text-secondary capitalize font-manrope font-medium m-0 border-x-0 border-y border-solid border-primary py-md">
        <div className="u-width-7 px-xs">
          {messages?.inventory?.labels.name}
        </div>
        <div className="u-width-2">
          {messages?.inventory?.labels.articleNumber}
        </div>
        <div className="u-width-2">{messages?.tickets?.quantity}</div>
        <div className="u-width-1" />
      </div>
      {addedParts.map(({ part, quantity, addedBy, stockOperation }, index) => {
        const hasAddedBy =
          addedBy &&
          showAddedBy &&
          (addedBy.role.toLowerCase() === "user" ||
            addedBy?.oem?._id !== user?.oem?._id);

        const isPendingAllocation =
          isInventoryManagementEnabled &&
          stockOperation?.allocationStatus === ALLOCATION_STATUS.PENDING;

        const handleAllocateClick = (e) => {
          e.stopPropagation();
          onAllocatePart(part, stockOperation);
          setActiveMenuPartId(null);
        };

        return (
          <div key={index} className="mt-md">
            <div
              className={`parts-table__col u-flex u-items-center u-padding-0 ${
                allowPreview ? "u-cursor-pointer" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                openPreview(part);
              }}
            >
              <div className="u-width-7 u-flex">
                <div className="relative mr-md h-4xl w-5xl shrink-0 overflow-visible">
                  <div className="h-full w-full overflow-hidden rounded-lg">
                    <img
                      src={getPartThumbnail(part)}
                      alt={part?.name}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  {String(part?.visibilityScope || "").toLowerCase() ===
                  "external" ? (
                    <ExternalIcon
                      size={EXTERNAL_ICON_SIZES.ACCORDION}
                      className={EXTERNAL_ICON_THUMB_BADGE_CLASS}
                    />
                  ) : null}
                </div>
                <div
                  className={`u-flex-grow-1 u-flex u-flex-column u-overflow-hidden ${
                    !hasAddedBy ? "u-justify-center" : ""
                  }`}
                >
                  <Label size={LABEL_SIZES.SMALL} className="truncate">
                    {part?.name}
                  </Label>
                  {hasAddedBy ? (
                    <span className="part-added-by-tag ">
                      <BodyText
                        size={BODY_TEXT_SIZES.X_SMALL}
                        color="text-brand"
                        className="uppercase text-center font-semibold"
                      >
                        {messages?.common?.customerOrder}
                      </BodyText>
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="u-width-2">
                <div className="parts-table-item__name u-flex-grow-1 u-text-overflow">
                  <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
                    {part?.articleNumber}
                  </BodyText>
                </div>
              </div>
              <div className="u-width-2">
                <div className="parts-table-item__name u-flex-grow-1 u-flex u-items-center gap-2">
                  <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
                    {quantity}
                  </BodyText>
                  {isPendingAllocation && (
                    <span
                      className="u-cursor-pointer u-flex u-items-center"
                      onClick={handleAllocateClick}
                      onMouseEnter={() => {
                        ReactTooltip.rebuild();
                      }}
                      data-tip={
                        messages?.tickets?.pendingAllocationTooltip ||
                        "This part was out of stock when ordered. You can allocate stock after it's restocked using this icon or the 'Allocate Part' from options."
                      }
                      data-for="global-tooltip"
                      data-event="mouseenter"
                      data-event-off="mouseleave"
                    >
                      <PendingAllocationWarningIcon />
                    </span>
                  )}
                </div>
              </div>
              {allowEdit && (
                <div className="u-width-1">
                  <div className="u-self-center u-flex u-justify-end u-items-center spare-part-list-item relative">
                    <span
                      className="u-cursor-pointer u-flex u-items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isClosedTicket) return;
                        setActiveMenuPartId(
                          activeMenuPartId === part?._id ? null : part?._id,
                        );
                      }}
                    >
                      <DotsThree
                        size={20}
                        className={`${isClosedTicket && "text-disabled"}`}
                        weight="bold"
                      />
                    </span>
                    {activeMenuPartId === part?._id && (
                      <div className="absolute top-full right-0 z-10">
                        <ActionPopup
                          actions={[
                            ...(isPendingAllocation
                              ? [
                                  {
                                    label: (
                                      <span className="flex items-center gap-2">
                                        <ArrowsClockwise size={16} />
                                        {messages?.tickets?.allocatePart ||
                                          "Allocate Part"}
                                      </span>
                                    ),
                                    onClick: handleAllocateClick,
                                    className: "allocate",
                                  },
                                ]
                              : []),
                            {
                              label: (
                                <span className="flex items-center gap-2">
                                  <PencilSimple size={16} />
                                  {messages?.inventory?.editQuantity ||
                                    "Edit Quantity"}
                                </span>
                              ),
                              onClick: (e) => {
                                e.stopPropagation();
                                handleEditPartClick(part, quantity);
                              },
                              className: "edit",
                            },
                            {
                              label: (
                                <span className="flex items-center gap-2">
                                  <MinusCircle size={16} />
                                  {messages?.inventory?.unassign || "Unassign"}
                                </span>
                              ),
                              onClick: (e) => {
                                e.stopPropagation();
                                handlePartRemoveClick({
                                  id: part?._id,
                                  name: part?.name,
                                });
                              },
                              className: "delete",
                            },
                          ]}
                          close={() => setActiveMenuPartId(null)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <AlertBox
        title={messages?.inventory?.unassignPartTitle}
        description={messages?.inventory?.unassignConfirmationFromWorkOrderTitle?.replace(
          "{{}}",
          showPartRemoveConfirmation?.name,
        )}
        isOpen={showPartRemoveConfirmation.show}
        zIndexClass={alertZIndexClass}
        acceptButtonText={
          messages?.inventory?.unassignConfirmationAcceptBtnText
        }
        cancelButtonText={
          messages?.inventory?.unassignConfirmationCencalBtnText
        }
        image={<RemoveGhostIcon width="130" height="130" />}
        onCancel={() => {
          setShowPartRemoveConfirmation({ show: false, name: "" });
          tempRemovePartId.current = null;
        }}
        onAccept={() => {
          handlePartRemove(tempRemovePartId.current);
          setShowPartRemoveConfirmation({ show: false, name: "" });
        }}
        overlay={true}
      />

      {showPartPreview && (
        <PartPreview
          data={partToPreview.current}
          onClose={() => {
            setShowPartPreview(false);
          }}
          showCustomFields={true}
          customAdditionalFields={partsCustomAdditionalFields}
          isOpen={showPartPreview}
          isSharedAssetsPage={isRequest}
        />
      )}
    </>
  );
};

export const CreateTicketAddedPartsComp = ({
  addedParts = [],
  handlePartRemove,
  handlePartQuantity,
  // z-index for the remove-confirmation alert. Override when this list is
  // rendered inside a sheet/overlay that sits above the default z-20.
  alertZIndexClass = "z-20",
}) => {
  const { messages } = useIntl();

  const [showPartRemoveConfirmation, setShowPartRemoveConfirmation] = useState({
    show: false,
    name: "",
  });
  const tempRemovePartId = useRef(null);

  const handlePartRemoveClick = (part) => {
    setShowPartRemoveConfirmation({ show: true, name: part.name });
    tempRemovePartId.current = part._key || part._id;
  };

  return (
    <>
      {addedParts.map(({ part, quantity, addedBy }, index) => {
        const partRef = part._key || part._id;
        return (
          <div key={index} className="mt-md">
            <PartListItem
              articleNumber={part?.articleNumber}
              parentAsset={
                part?.assetName && part?.assetSerialNumber
                  ? `${part.assetName} • ${part.assetSerialNumber}`
                  : ""
              }
              name={part?.name}
              thumbnail={getPartThumbnail(part)}
              visibilityScope={part?.visibilityScope}
              hideDetailsButton
              showDeleteButton
              onDeleteButtonClick={() => handlePartRemoveClick(part)}
              counterProps={{
                count: quantity,
                incrementCount: () => handlePartQuantity(partRef, quantity + 1),
                decrementCount: () => handlePartQuantity(partRef, quantity - 1),
              }}
            />
          </div>
        );
      })}

      <AlertBox
        title={messages?.inventory?.unassignPartTitle}
        description={messages?.inventory?.unassignConfirmationFromWorkOrderTitle?.replace(
          "{{}}",
          showPartRemoveConfirmation?.name,
        )}
        isOpen={showPartRemoveConfirmation.show}
        zIndexClass={alertZIndexClass}
        acceptButtonText={
          messages?.inventory?.unassignConfirmationAcceptBtnText
        }
        cancelButtonText={
          messages?.inventory?.unassignConfirmationCencalBtnText
        }
        image={<RemoveGhostIcon width="130" height="130" />}
        onCancel={() => {
          setShowPartRemoveConfirmation({ show: false, name: "" });
          tempRemovePartId.current = null;
        }}
        onAccept={() => {
          handlePartRemove(tempRemovePartId.current);
          setShowPartRemoveConfirmation({ show: false, name: "" });
        }}
        overlay={true}
      />
    </>
  );
};

export const AllocatePartComp = ({ part, quantity }) => {
  const { messages } = useIntl();

  // The allocation quantity is the quantity assigned to the work order
  const allocationQuantity = quantity || 0;

  const getAvailableStock = useCallback(() => {
    const rawValue = part?.availableStock;
    if (rawValue === null || rawValue === undefined) {
      return 0;
    }
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.max(0, numericValue);
  }, [part]);

  const availableStock = getAvailableStock();
  const hasInsufficientStock = allocationQuantity > availableStock;

  const getStockErrorMessage = () => {
    if (!hasInsufficientStock) return "";

    const template = messages?.tickets?.insufficientStockAllocation;
    const message = template?.replace(
      /{{count}}|{count}/g,
      availableStock.toString(),
    );

    if (
      message === template &&
      !template?.includes("{{count}}") &&
      !template?.includes("{count}")
    ) {
      return `Insufficient Stock: Please be aware, there are only ${availableStock} parts available in inventory. Please restock to allocate parts to this work order.`;
    }

    return message;
  };

  const errorMessage = getStockErrorMessage();

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex items-start gap-lg">
        <div className="relative h-4xl w-5xl shrink-0 overflow-visible">
          <div className="h-full w-full overflow-hidden rounded-lg">
            <img
              src={getPartThumbnail(part)}
              alt={part?.name}
              className="h-full w-full object-cover object-center"
            />
          </div>
          {String(part?.visibilityScope || "").toLowerCase() === "external" ? (
            <ExternalIcon
              size={EXTERNAL_ICON_SIZES.ACCORDION}
              className={EXTERNAL_ICON_THUMB_BADGE_CLASS}
            />
          ) : null}
        </div>
        <div className="flex flex-col flex-grow overflow-hidden">
          <Label size={LABEL_SIZES.MEDIUM} className="truncate mb-xs">
            {part?.name}
          </Label>
          <div className="flex items-center gap-sm flex-wrap">
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
              {part?.articleNumber}
            </BodyText>
            <span className="text-secondary">•</span>
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
              {messages?.inventory?.stock?.availableStock || "Available Stock"}:{" "}
              {availableStock}
            </BodyText>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-sm">
        <InputLabel
          label={messages?.tickets?.allocation || "Allocation"}
          keyId="allocation-quantity"
          uppercaseLabel={false}
        />
        <InputBox
          keyId="allocation-quantity"
          value={allocationQuantity}
          disabled={true}
          hasError={hasInsufficientStock}
          className="w-full"
        />
        {hasInsufficientStock && (
          <div className="flex items-start gap-sm">
            <WarningCircle
              size={16}
              className="text-danger flex-shrink-0 mt-[2px]"
            />
            <BodyText
              size={BODY_TEXT_SIZES.X_SMALL}
              color="text-danger"
              className="flex-grow"
            >
              {errorMessage}
            </BodyText>
          </div>
        )}
      </div>
    </div>
  );
};
